// @file Store for handling Guest ID Card/Modal/Label on the surface
import { getTurnstileVerificationToken, resetTurnstile } from '@@/bits/cloudflare_turnstile'
import { getCookie } from '@@/bits/cookie'
import { captureFetchException } from '@@/bits/error_tracker'
import { isAppUsing } from '@@/bits/flip'
import { __ } from '@@/bits/intl'
import { anonymous } from '@@/bits/user_model'
import { HttpCode, SnackbarNotificationType } from '@@/enums'
import { useGlobalSnackbarStore } from '@@/pinia/global_snackbar'
import { useSurfaceStore } from '@@/pinia/surface'
import { useSurfacePermissionsStore } from '@@/pinia/surface_permissions'
import { useSurfaceUserContributorsStore } from '@@/pinia/surface_user_contributors'
import PadletApi from '@@/surface/padlet_api'
import { User } from '@@/types'
import { configure as configureArvo } from '@padlet/arvo'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

export const useSurfaceGuestStore = defineStore('surfaceGuestStore', () => {
  const surfaceStore = useSurfaceStore()
  const surfacePermissionsStore = useSurfacePermissionsStore()
  const globalSnackbarStore = useGlobalSnackbarStore()
  const surfaceUserContributors = useSurfaceUserContributorsStore()

  const xGuestIdCard = ref<boolean>(false)
  const xGuestIdModal = ref<boolean>(false)
  const xChangeGuestModal = ref<boolean>(false)
  const userName = ref<string>('')
  const cloudflareTurnstileWidgetId = ref<string>('')
  const afterSaveNameActions = ref<Array<() => void>>([])
  const userNameErrorMessage = ref<string>('')
  const isAwaitingServerResponse = ref(false)
  const userNameHasViolation = ref<boolean | null>(null)

  // Getters

  const displayName = computed<string>(() => {
    const sessionUserName = getCookie('session_user_name')?.replace(/[^a-zA-Z]/g, ' ')
    const name = surfaceStore.user.name !== '' ? surfaceStore.user.name : sessionUserName
    return name !== '' ? name : anonymous
  })
  const userAvatar = computed<string>(() => surfaceStore.user.avatar)
  const isUserNameUpdated = computed<boolean>(() => surfaceStore.user.name === displayName.value)
  const shouldShowGuestIdModal = computed<boolean>(
    () => isAppUsing('anonymousAttribution') && !surfacePermissionsStore.amIRegistered && !isUserNameUpdated.value,
  )
  const isUserNameValid = computed((): boolean => {
    if (userNameErrorMessage.value !== '') return false
    if (userNameHasViolation.value === true) return false
    return true
  })

  // Actions
  function showGuestIdCard(): void {
    xGuestIdCard.value = true
  }

  function hideGuestIdCard(): void {
    xGuestIdCard.value = false
  }

  function showGuestIdModal(options?: { afterSaveActions?: Array<() => void> }): void {
    xGuestIdModal.value = true
    if (options?.afterSaveActions != null) {
      afterSaveNameActions.value.push(...options?.afterSaveActions)
    }
  }

  function hideGuestIdModal(): void {
    xGuestIdModal.value = false
    afterSaveNameActions.value = []
  }

  function showChangeGuestModal(): void {
    xChangeGuestModal.value = true
  }

  function hideChangeGuestModal(): void {
    xChangeGuestModal.value = false
  }

  function setUserName(name: string): void {
    userName.value = name
    userNameErrorMessage.value = ''
    userNameHasViolation.value = null
  }

  function setCloudflareTurnstileWidgetId(id: string): void {
    cloudflareTurnstileWidgetId.value = id
  }

  async function updateSessionUserName(): Promise<void> {
    const trimmedUserName = userName.value.trim()

    if (trimmedUserName === '' || trimmedUserName === surfaceStore.user.name) return
    try {
      isAwaitingServerResponse.value = true

      await moderateUserName(trimmedUserName)
      if (userNameHasViolation.value !== false) return

      const user = await PadletApi.Powwow.updateSessionUserName({ userId: surfaceStore.user.id, name: trimmedUserName })
      updateUserInSurfaceStores(user)
    } catch (e) {
      if (e.status === HttpCode.UnprocessableEntity) {
        userNameErrorMessage.value = __('Name invalid')
      } else {
        captureFetchException(e, { source: 'SurfaceGuestStoreUpdateSessionUserName' })
        globalSnackbarStore.setSnackbar({
          message: __('Error updating name'),
          notificationType: SnackbarNotificationType.error,
        })
      }
    } finally {
      isAwaitingServerResponse.value = false
    }
  }

  async function changeGuest(): Promise<void> {
    const trimmedUserName = userName.value.trim()

    try {
      isAwaitingServerResponse.value = true

      await moderateUserName(trimmedUserName)
      if (userNameHasViolation.value !== false) return

      const { user, arvoConfig } = await PadletApi.Powwow.createSessionUser({
        humanVerificationToken: getTurnstileVerificationToken(cloudflareTurnstileWidgetId.value),
        name: trimmedUserName,
      })
      updateUserInSurfaceStores(user)
      void configureArvo(arvoConfig as any)
      resetTurnstile(cloudflareTurnstileWidgetId.value) // Reset turnstile to get a new token

      surfaceStore.refetchStartingStateAndCreateNewBrahmsConnection()
      globalSnackbarStore.setSnackbar({
        message: __('Guest changed to %{userName}', { userName: user.name }),
        notificationType: SnackbarNotificationType.success,
      })
    } catch (e) {
      if (e.status === HttpCode.UnprocessableEntity) {
        userNameErrorMessage.value = __('Name invalid')
      } else {
        captureFetchException(e, { source: 'SurfaceGuestStoreCreateSessionUserName' })
        globalSnackbarStore.setSnackbar({
          message: __('Error changing guest'),
          notificationType: SnackbarNotificationType.error,
        })
      }
    } finally {
      isAwaitingServerResponse.value = false
    }
  }

  async function handleSessionExpired(): Promise<void> {
    try {
      const { user, arvoConfig } = await PadletApi.Powwow.createSessionUser({
        humanVerificationToken: getTurnstileVerificationToken(cloudflareTurnstileWidgetId.value),
      })
      updateUserInSurfaceStores(user)
      void configureArvo(arvoConfig as any)
      resetTurnstile(cloudflareTurnstileWidgetId.value) // Reset turnstile to get a new token
      surfaceStore.refetchStartingStateAndCreateNewBrahmsConnection()
    } catch (e) {
      captureFetchException(e, { source: 'SurfaceGuestStoreHandleSessionExpired' })
    }
  }

  async function moderateUserName(name: string): Promise<void> {
    try {
      const { userNameHasViolation: hasViolation } = await PadletApi.ContentModeration.moderateUserName({
        name,
      })
      userNameHasViolation.value = hasViolation === null ? null : hasViolation

      if (userNameHasViolation.value === true) {
        globalSnackbarStore.setSnackbar({
          message: __('Your name was flagged as inappropriate and was not updated'),
          notificationType: SnackbarNotificationType.error,
        })
      } else if (userNameHasViolation.value === null) {
        globalSnackbarStore.genericFetchError()
      }
    } catch (e) {
      captureFetchException(e, { source: 'SurfaceGuestStoreModerateUserName' })
    }
  }

  function updateUserInSurfaceStores(user: User): void {
    surfaceUserContributors.updateUser(user)
    surfaceStore.updateUser(user)
  }

  return {
    // State
    xGuestIdCard,
    xGuestIdModal,
    xChangeGuestModal,
    userName,
    cloudflareTurnstileWidgetId,
    afterSaveNameActions,
    userNameErrorMessage,
    isAwaitingServerResponse,

    // Getters
    displayName,
    userAvatar,
    isUserNameUpdated,
    shouldShowGuestIdModal,
    isUserNameValid,

    // Actions
    showGuestIdCard,
    hideGuestIdCard,
    showGuestIdModal,
    hideGuestIdModal,
    setUserName,
    setCloudflareTurnstileWidgetId,
    updateSessionUserName,
    changeGuest,
    showChangeGuestModal,
    hideChangeGuestModal,
    handleSessionExpired,
    moderateUserName,
  }
})
